import React from "react";
import { useNavigate } from "react-router-dom";
import Container from "@/components/foundation/Container";
import Button from "@/components/foundation/Button";
import Badge from "@/components/foundation/Badge";
import FadeIn from "@/components/foundation/FadeIn";
import GlowOrb from "@/components/foundation/GlowOrb";
import GridPattern from "@/components/foundation/GridPattern";
import ParticleField from "@/components/foundation/ParticleField";
import MerchantWaitlist from "@/components/marketing/MerchantWaitlist";
import PhoneMockup from "@/components/marketing/PhoneMockup";
import DashboardScreen from "@/components/marketing/AppScreens/DashboardScreen";
import PartnerLink from "@/components/marketing/PartnerLink";
import HeroFundingCard from "@/components/marketing/HeroFundingCard";
import { ArrowRight, Play } from "lucide-react";

export const HeroSection = ({ onQualifyClick }) => {
  const navigate = useNavigate();

  const scrollToZenscore = () => {
    const el = document.getElementById("zenscore-preview");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      className="relative flex min-h-screen items-center overflow-hidden pt-24 sm:pt-28"
      data-testid="hero-section"
    >
      {/* Background layers */}
      <ParticleField density={0.28} color="rgba(0,230,118,0.5)" />
      <GridPattern variant="dots" size={30} opacity={0.35} />
      <GlowOrb
        color="emerald"
        size={720}
        intensity={0.26}
        className="-bottom-40 -left-40"
      />
      <GlowOrb
        color="emerald"
        size={520}
        intensity={0.12}
        className="top-24 right-[-140px]"
        animate={false}
      />

      <Container className="relative z-10 pb-16 pt-8 lg:pb-24">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-8">
          {/* LEFT — copy + CTAs */}
          <div className="lg:col-span-7">
            <FadeIn>
              <div
                className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-text-secondary"
                data-testid="hero-eyebrow"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald shadow-[0_0_10px_rgba(0,230,118,0.7)]" />
                <span>
                  Powered by{" "}
                  <PartnerLink partner="finicity" showGlyph={false} className="!text-inherit hover:!text-brand-emerald">
                    Mastercard Finicity
                  </PartnerLink>
                </span>
                <span className="text-text-muted">·</span>
                <span>
                  Built with{" "}
                  <PartnerLink partner="uptiq" showGlyph={false} className="!text-inherit hover:!text-brand-emerald">
                    Uptiq AI
                  </PartnerLink>
                </span>
              </div>
            </FadeIn>

            <FadeIn delay={0.08}>
              <h1
                className="mt-6 font-display text-5xl font-semibold leading-[1.05] tracking-tight text-white text-balance sm:text-6xl lg:text-[72px] xl:text-[84px]"
                data-testid="hero-headline"
              >
                Business funding built around your{" "}
                <span className="relative whitespace-nowrap">
                  <span className="relative z-10 bg-gradient-to-r from-brand-emerald via-brand-emerald to-[#7CFFC0] bg-clip-text text-transparent">
                    cash flow.
                  </span>
                </span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.16}>
              <p
                className="mt-6 max-w-xl text-base leading-relaxed text-text-secondary sm:text-lg"
                data-testid="hero-subheadline"
              >
                Securely connect your business banking, receive your AI-powered
                ZenScore, and unlock personalized funding opportunities — all
                from one intelligent platform.
              </p>
            </FadeIn>

            <FadeIn delay={0.20}>
              <div className="mt-8 max-w-xl">
                <HeroFundingCard />
                <p
                  className="mt-3 max-w-xl text-xs italic leading-relaxed text-text-muted"
                  data-testid="hero-funding-disclosure"
                >
                  *Funding amounts, offers and timing vary based on business
                  qualifications, underwriting, application completeness and
                  funding partner approval. Funding is not guaranteed.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.28}>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={onQualifyClick}
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                  data-testid="hero-qualify-btn"
                >
                  See What You Qualify For
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={scrollToZenscore}
                  leftIcon={<Play className="h-3.5 w-3.5" fill="currentColor" />}
                  data-testid="hero-how-it-works-btn"
                >
                  See How It Works
                </Button>
              </div>
            </FadeIn>

            <FadeIn delay={0.38}>
              <div className="mt-10 max-w-lg">
                <p className="mb-3 text-xs uppercase tracking-[0.14em] text-text-muted">
                  Or start with your business email.
                </p>
                <MerchantWaitlist
                  size="md"
                  source="hero_inline_qualify"
                  ctaLabel="See What You Qualify For"
                  successMessage="Thanks — we'll be in touch shortly with next steps."
                  data-testid="hero-waitlist"
                />
              </div>
            </FadeIn>
          </div>

          {/* RIGHT — phone mockup */}
          <div className="relative flex items-center justify-center lg:col-span-5 lg:justify-end">
            <FadeIn delay={0.2} direction="left">
              <PhoneMockup width={340} data-testid="hero-phone">
                <DashboardScreen />
              </PhoneMockup>
            </FadeIn>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default HeroSection;
